// src/pages/RegedOrderList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchOrders, deletePerformas } from "../actions/performaActions";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import moment from "moment-jalaali";
import Select from "../components/ui/Select";
import PaginationControls from "../components/PaginationControls";
import { formatNumber } from "../utils/numberFormat";
import HotToastMessage from "../components/ui/HotToastMessage";
import ConfirmModal from "../components/ui/ConfirmModal";
import FilterSlideOver, {
  AccordionItem,
} from "../components/ui/FilterSlideOver";
import {
  Filter as FilterIcon,
  RefreshCw,
  Trash2,
  X,
  MoveDown,
  MoveUp,
  Search,
} from "lucide-react";

moment.loadPersian({ dialect: "persian-modern" });

const RegedOrderList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { orders, loading, error, count, next, previous } = useSelector(
    (state) => state.order
  );
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (auth.isAuthenticated === false) navigate("/");
  }, [auth.isAuthenticated, navigate]);

  // Search (immediate UI) + debounced query (API)
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [currency, setCurrency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Sorting
  const [sortConfig, setSortConfig] = useState({ ordering: "" });

  // Selection / bulk actions
  const [selectedOrders, setSelectedOrders] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // UI
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [showModal, setShowModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const currencyOptions = useMemo(
    () => [
      { value: "یورو", label: "یورو" },
      { value: "دلار", label: "دلار" },
      { value: "ریال", label: "ریال" },
      { value: "یوان", label: "یوان" },
      { value: "درهم", label: "درهم" },
    ],
    []
  );

  const statusOptions = useMemo(() => {
    const unique = [
      ...new Set((orders || []).map((o) => o.prf_status).filter(Boolean)),
    ];
    return [
      { value: "", label: "همه وضعیت‌ها" },
      ...unique.map((s) => ({ value: s, label: s })),
    ];
  }, [orders]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const buildFilters = () => ({
    search: query,
    status: statusFilter,
    startDate,
    endDate,
    currency,
    ...(sortConfig.ordering ? { ordering: sortConfig.ordering } : {}),
  });

  useEffect(() => {
    dispatch(fetchOrders(currentPage, pageSize, buildFilters()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    currentPage,
    pageSize,
    query,
    statusFilter,
    startDate,
    endDate,
    currency,
    sortConfig.ordering,
  ]);

  const handleStartDateChange = (date) => {
    setStartDate(date && date.format ? date.format("YYYY-MM-DD") : "");
    setCurrentPage(1);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date && date.format ? date.format("YYYY-MM-DD") : "");
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      const isAscendingNow = prev.ordering === field;
      return { ordering: isAscendingNow ? `-${field}` : field };
    });
    setCurrentPage(1);
  };

  const totalPages = count ? Math.ceil(count / pageSize) : 1;
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSelectOrder = (event, order) => {
    const { checked } = event.target;
    setSelectedOrders((prev) =>
      checked
        ? [...prev, order.prfVCodeInt]
        : prev.filter((id) => id !== order.prfVCodeInt)
    );
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setSelectedOrders(checked ? (orders || []).map((o) => o.prfVCodeInt) : []);
  };

  const handleDeleteSelectedOrders = () => {
    if (!selectedOrders.length) {
      setToast({
        message: "لطفاً حداقل یک سفارش را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    setShowModal(false);
    dispatch(deletePerformas(selectedOrders))
      .then(() => {
        setToast({
          message: "سفارش‌های انتخاب شده با موفقیت حذف شدند.",
          type: "success",
        });
        setSelectedOrders([]);
        dispatch(fetchOrders(currentPage, pageSize, buildFilters()));
      })
      .catch(() =>
        setToast({ message: "خطا در حذف سفارش‌ها.", type: "error" })
      );
  };

  const handleCancelDelete = () => setShowModal(false);

  const clearFilters = () => {
    setStatusFilter("");
    setCurrency("");
    setStartDate("");
    setEndDate("");
    setSortConfig({ ordering: "" });
    setCurrentPage(1);
  };

  const activeFilterCount = useMemo(() => {
    return [
      statusFilter,
      currency,
      startDate,
      endDate,
      sortConfig.ordering,
    ].filter(Boolean).length;
  }, [statusFilter, currency, startDate, endDate, sortConfig.ordering]);

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderRadius: 12,
        borderColor: state.isFocused ? "#cbd5e1" : "#e2e8f0",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(59,130,246,0.12)" : "none",
        textAlign: "right",
      }),
      menu: (base) => ({ ...base, textAlign: "right" }),
      option: (base) => ({ ...base, textAlign: "right" }),
      placeholder: (base) => ({ ...base, color: "#94a3b8" }),
      singleValue: (base) => ({ ...base, direction: "rtl" }),
      input: (base) => ({ ...base, direction: "rtl" }),
    }),
    []
  );

  return (
    <div className="mt-24 pb-10 bg-slate-50 min-h-screen font-vazir" dir="rtl">
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Filters SlideOver */}
      <FilterSlideOver
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلترها"
        badgeCount={activeFilterCount}
        onClear={() => {
          clearFilters();
        }}
        onApply={() => {
          // filters are already bound to state; just close
          setFiltersOpen(false);
          setCurrentPage(1);
        }}
      >
        <AccordionItem title="وضعیت و ارز" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 text-right mb-2">
                وضعیت
              </label>
              <Select
                value={
                  statusOptions.find((o) => o.value === statusFilter) || null
                }
                onChange={(opt) => {
                  setStatusFilter(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                options={statusOptions}
                isClearable
                isLoading={loading}
                placeholder={loading ? "در حال بارگذاری..." : "همه وضعیت‌ها"}
                noOptionsMessage={() =>
                  loading ? "در حال بارگذاری..." : "موردی یافت نشد"
                }
                styles={selectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-right mb-2">
                ارز
              </label>
              <Select
                value={
                  currencyOptions.find((o) => o.value === currency) || null
                }
                onChange={(opt) => {
                  setCurrency(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                options={currencyOptions}
                isClearable
                placeholder="انتخاب ارز"
                styles={selectStyles}
              />
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="بازه تاریخ" defaultOpen>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 text-right mb-2">
                تاریخ شروع
              </label>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ شروع"
                clearable
                style={{
                  width: "100%",
                  borderRadius: "12px",
                  borderColor: "#e2e8f0",
                  padding: "10px 12px",
                  height: "42px",
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-right mb-2">
                تاریخ پایان
              </label>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ پایان"
                clearable
                style={{
                  width: "100%",
                  borderRadius: "12px",
                  borderColor: "#e2e8f0",
                  padding: "10px 12px",
                  height: "42px",
                }}
              />
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="مرتب‌سازی" defaultOpen={false}>
          <div className="text-sm text-slate-700 space-y-2">
            <p className="text-slate-600">
              مرتب‌سازی از طریق کلیک روی ستون‌های جدول انجام می‌شود.
            </p>
            {sortConfig.ordering ? (
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span>Ordering:</span>
                <span className="font-medium">{sortConfig.ordering}</span>
              </div>
            ) : (
              <div className="text-slate-500">مرتب‌سازی فعالی وجود ندارد.</div>
            )}
            <button
              type="button"
              onClick={() => setSortConfig({ ordering: "" })}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50"
            >
              پاک کردن مرتب‌سازی
            </button>
          </div>
        </AccordionItem>
      </FilterSlideOver>

      {/* Page shell (more modern, less glassy) */}
      <div className="mx-auto max-w-7xl md:max-w-[90%] px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="text-right">
              <h2 className="text-2xl font-extrabold text-slate-900">
                لیست سفارش‌های ثبت‌شده
              </h2>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  تعداد: {count ?? 0}
                </span>
                {activeFilterCount ? (
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(true)}
                    className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700 hover:bg-blue-100"
                    title="مشاهده فیلترهای فعال"
                  >
                    {activeFilterCount} فیلتر فعال
                  </button>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    بدون فیلتر
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  dispatch(fetchOrders(currentPage, pageSize, buildFilters()))
                }
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                بروزرسانی
              </button>

              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                <FilterIcon className="h-4 w-4" />
                فیلترها
              </button>

              <button
                type="button"
                onClick={handleDeleteSelectedOrders}
                disabled={!selectedOrders.length}
                className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4" />
                حذف انتخاب‌شده‌ها
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-700 text-right">
              جستجو (شماره سفارش/نام فروشنده/وضعیت)
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="جستجو..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="
                  w-full rounded-2xl border border-slate-200 bg-white px-4 py-3
                  pr-11 text-sm text-slate-800
                  outline-none transition
                  focus:ring-4 focus:ring-blue-100
                "
              />
              <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              {searchText ? (
                <button
                  type="button"
                  onClick={() => setSearchText("")}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-1 hover:bg-slate-100"
                  title="پاک کردن"
                  aria-label="پاک کردن"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Loading / Error */}
        <div className="mt-4">
          {loading ? (
            <div className="rounded-2xl bg-white p-6 text-center text-blue-600 ring-1 ring-slate-200">
              در حال بارگذاری...
            </div>
          ) : error ? (
            <div className="rounded-2xl bg-white p-6 text-center text-red-600 ring-1 ring-slate-200">
              {error}
            </div>
          ) : null}
        </div>

        {/* Table & Pagination */}
        {!loading && !error && (
          <div className="mt-4 space-y-4">
            {orders.length === 0 ? (
              <div className="rounded-2xl bg-white p-8 text-center text-slate-600 ring-1 ring-slate-200">
                هیچ سفارشی با این جستجو و فیلترها یافت نشد.
              </div>
            ) : (
              <>
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOptions={[10, 20, 50, 100, 200]}
                  hasNext={hasNext}
                  hasPrevious={hasPrevious}
                  onPageChange={handlePageChange}
                  onNextPage={handleNextPage}
                  onPreviousPage={handlePreviousPage}
                  onPageSizeChange={handlePageSizeChange}
                />

                <div className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1100px] text-sm">
                      <thead className="bg-slate-50 text-slate-700">
                        <tr>
                          <th className="p-4 text-right">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                selectedOrders.length === orders.length &&
                                orders.length > 0
                              }
                            />
                          </th>

                          <th className="p-4 text-right font-semibold">ردیف</th>

                          <th className="p-4 text-right font-semibold">
                            شماره سفارش
                          </th>

                          <th
                            onClick={() => handleSort("prf_date")}
                            className="p-4 text-right font-semibold cursor-pointer hover:bg-slate-100"
                          >
                            <span className="inline-flex items-center gap-2">
                              تاریخ سفارش
                              {sortConfig.ordering === "prf_date" && (
                                <MoveUp className="h-4 w-4 text-blue-600" />
                              )}
                              {sortConfig.ordering === "-prf_date" && (
                                <MoveDown className="h-4 w-4 text-blue-600" />
                              )}
                            </span>
                          </th>

                          <th
                            onClick={() => handleSort("prf_expire_date")}
                            className="p-4 text-right font-semibold cursor-pointer hover:bg-slate-100"
                          >
                            <span className="inline-flex items-center gap-2">
                              تاریخ اعتبار سفارش
                              {sortConfig.ordering === "prf_expire_date" && (
                                <MoveUp className="h-4 w-4 text-blue-600" />
                              )}
                              {sortConfig.ordering === "-prf_expire_date" && (
                                <MoveDown className="h-4 w-4 text-blue-600" />
                              )}
                            </span>
                          </th>

                          <th
                            onClick={() => handleSort("prf_total_price")}
                            className="p-4 text-right font-semibold cursor-pointer hover:bg-slate-100"
                          >
                            <span className="inline-flex items-center gap-2">
                              مبلغ کل
                              {sortConfig.ordering === "prf_total_price" && (
                                <MoveUp className="h-4 w-4 text-blue-600" />
                              )}
                              {sortConfig.ordering === "-prf_total_price" && (
                                <MoveDown className="h-4 w-4 text-blue-600" />
                              )}
                            </span>
                          </th>

                          <th
                            onClick={() => handleSort("remaining_total")}
                            className="p-4 text-right font-semibold cursor-pointer hover:bg-slate-100"
                          >
                            <span className="inline-flex items-center gap-2">
                              باقی مانده
                              {sortConfig.ordering === "remaining_total" && (
                                <MoveUp className="h-4 w-4 text-blue-600" />
                              )}
                              {sortConfig.ordering === "-remaining_total" && (
                                <MoveDown className="h-4 w-4 text-blue-600" />
                              )}
                            </span>
                          </th>

                          <th className="p-4 text-right font-semibold">
                            وضعیت
                          </th>
                          <th className="p-4 text-right font-semibold">
                            وضعیت تخصیص ارز
                          </th>
                          <th className="p-4" />
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {orders.map((order, index) => (
                          <tr key={order.id} className="hover:bg-slate-50">
                            <td className="p-4 text-right">
                              <input
                                type="checkbox"
                                checked={selectedOrders.includes(
                                  order.prfVCodeInt
                                )}
                                onChange={(e) => handleSelectOrder(e, order)}
                              />
                            </td>

                            <td className="p-4 text-right">
                              {(currentPage - 1) * pageSize + index + 1}
                            </td>

                            <td className="p-4 text-right">
                              <Link
                                className="text-blue-700 hover:text-blue-900 border-b border-blue-200 hover:border-blue-400"
                                to={`/order-details/${order.prfVCodeInt}`}
                              >
                                {order.prf_order_no}
                              </Link>
                            </td>

                            <td className="p-4 text-right">{order.prf_date}</td>
                            <td className="p-4 text-right">
                              {order.prf_expire_date}
                            </td>

                            <td className="p-4 text-right">
                              {formatNumber(order.prf_total_price)}
                            </td>

                            <td className="p-4 text-right">
                              {order.remaining_total > -1
                                ? `باقی مانده: ${formatNumber(
                                    order.remaining_total
                                  )}`
                                : `مابه‌تفاوت: ${formatNumber(
                                    Math.abs(order.remaining_total)
                                  )}`}
                            </td>

                            <td className="p-4 text-right">
                              {order.prf_status || "—"}
                            </td>

                            <td className="p-4 text-right">
                              {order.currency_allocation_status || "—"}
                            </td>

                            <td className="p-4" />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOptions={[10, 20, 50, 100, 200]}
                  hasNext={hasNext}
                  hasPrevious={hasPrevious}
                  onPageChange={handlePageChange}
                  onNextPage={handleNextPage}
                  onPreviousPage={handlePreviousPage}
                  onPageSizeChange={handlePageSizeChange}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegedOrderList;
